/**
 * Client Environment Singleton
 *
 * Provides the ClientEnv singleton for browser contexts.
 * Supports two initialization patterns:
 * - React tree: ClientEnvProvider calls initializeClientEnv()
 * - Early access: getClientEnv() lazily initializes from __CLIENT_CONFIG__
 *
 * @example
 * ```typescript
 * // In React components (preferred):
 * const { config, logger, services } = useClientEnv()
 *
 * // Outside React tree (e.g., global error boundary):
 * import { getClientEnv } from '@/env/client'
 * const { logger, services } = getClientEnv()
 * ```
 *
 * @see src/app/hooks/use-client-env.tsx for the React provider and hook
 * @see docs/config-management.md for detailed documentation
 */

import type { ClientConfig, ClientWapiConfig } from '@/config/schema'
import { initializeClientLogger, initializeDatadog } from '@/observability/client'
import { baseLogger } from '@/observability/logger/base'
import type { Logger } from '@/observability/logger/types'
import type {
    ClientLoggerInitConfig,
    DatadogClient,
    DatadogInitConfig,
} from '@/observability/types'
import {
    createClientWapiService,
    type ClientWapiService,
} from '@/services/auth/client/wapi-service'

import { getGlobal, KEYS, setGlobal } from './globals'

// =============================================================================
// TYPES
// =============================================================================

export interface ClientEnv {
    config: ClientConfig
    logger: Logger
    services: {
        datadog: DatadogClient
        wapi: ClientWapiService
    }
}

// =============================================================================
// GLOBALS
// =============================================================================

declare global {
    var __CLIENT_ENV__: ClientEnv | undefined
    var __CLIENT_CONFIG__: ClientConfig | undefined
}

// =============================================================================
// FACTORIES
// =============================================================================

/**
 * Factory functions for creating service instances.
 * Each factory receives only the config subset it needs.
 */
export interface ClientEnvServiceFactories {
    logger?: (config: ClientLoggerInitConfig) => Logger
    datadog?: (config: DatadogInitConfig) => DatadogClient
    wapi?: (config: ClientWapiConfig) => ClientWapiService
}

/**
 * Default factories that create the standard service implementations.
 *
 * Note: Datadog SDK adds ~60KB gzipped to the client bundle. If Core Web Vitals
 * become a concern, consider lazy-loading via requestIdleCallback.
 */
export const defaultClientEnvFactories: Required<ClientEnvServiceFactories> = {
    logger: (config) => initializeClientLogger(config),
    datadog: (config) => initializeDatadog(config),
    wapi: (config) => createClientWapiService(config),
}

// =============================================================================
// CORE FUNCTIONS
// =============================================================================

/**
 * Derives the first-party analytics collector hosts (Snowplow + Amplitude) that the
 * Datadog logs `beforeSend` uses to drop blocked, status-0 beacons — from the same
 * per-env config the analytics SDKs use, so dev/pilot/prod each match their own
 * hosts and the filter can't drift out of sync with a hardcoded list. The Snowplow
 * `endpoint` is a bare host; the Amplitude `serverUrl` / optional
 * `remoteConfigServerUrl` are full URLs reduced to their hostname. Deduped.
 */
function deriveAnalyticsHosts(config: ClientConfig): string[] {
    const hosts = new Set<string>()
    if (config.snowplow.endpoint) {
        hosts.add(config.snowplow.endpoint)
    }
    for (const url of [config.amplitude.serverUrl, config.amplitude.remoteConfigServerUrl]) {
        if (!url) {
            continue
        }
        try {
            hosts.add(new URL(url).hostname)
        } catch {
            // Non-URL value — skip rather than throw during env composition.
        }
    }
    return [...hosts]
}

/**
 * Creates a ClientEnv instance from config.
 * This is a pure function - no async operations, no side effects.
 *
 * Exported for testing to create isolated instances without touching the singleton.
 *
 * @example
 * ```typescript
 * const mockConfig = { ... }
 * const env = createClientEnv(mockConfig, {
 *     datadog: () => mockDatadog,
 * })
 * ```
 */
export function createClientEnv(
    config: ClientConfig,
    factories: ClientEnvServiceFactories = {},
): ClientEnv {
    const resolvedFactories = { ...defaultClientEnvFactories, ...factories }

    const loggerConfig: ClientLoggerInitConfig = {
        logs: config.logs,
        datadog: { logs: config.datadog.logs },
    }

    const datadogConfig: DatadogInitConfig = {
        env: config.env,
        service: config.service,
        datadog: config.datadog,
        analyticsHosts: deriveAnalyticsHosts(config),
    }

    return {
        config,
        logger: resolvedFactories.logger(loggerConfig),
        services: {
            datadog: resolvedFactories.datadog(datadogConfig),
            wapi: resolvedFactories.wapi(config.wapi),
        },
    }
}

/**
 * Initializes the ClientEnv singleton from provided config.
 *
 * Called by ClientEnvProvider when it first renders. Subsequent calls
 * with the same config return the existing instance.
 *
 * Uses globalThis to persist across HMR and Turbopack module reloads in dev mode.
 *
 * @param config - The client configuration
 * @param factories - Optional custom service factories
 * @returns The initialized ClientEnv
 */
export function initializeClientEnv(
    config: ClientConfig,
    factories?: ClientEnvServiceFactories,
): ClientEnv {
    const existing = getGlobal<ClientEnv>(KEYS.CLIENT_ENV)
    if (existing) {
        return existing
    }

    const env = createClientEnv(config, factories)
    setGlobal(KEYS.CLIENT_ENV, env)
    // Use baseLogger (not env.logger) for the init log so SSR construction
    // doesn't trip the sentinel — `env.logger` is the SSR sentinel under
    // typeof window === 'undefined'.
    baseLogger
        .getLogger({ module: 'client-init' })
        .withMetadata({ env: config.env })
        .debug('ClientEnv initialized')
    return env
}

/**
 * Returns the ClientEnv singleton.
 *
 * This function supports lazy initialization: if ClientEnv hasn't been initialized
 * but __CLIENT_CONFIG__ is available (set by an inline script in the layout),
 * it will initialize ClientEnv automatically. This ensures ClientEnv is available
 * even in code that runs before the React tree mounts (e.g., global error boundary).
 *
 * @throws Error if neither ClientEnv nor ClientConfig is available
 */
export function getClientEnv(): ClientEnv {
    const env = getGlobal<ClientEnv>(KEYS.CLIENT_ENV)
    if (env) {
        return env
    }

    // Lazy initialization from config set by inline script in layout
    const config = getGlobal<ClientConfig>(KEYS.CLIENT_CONFIG)
    if (config) {
        return initializeClientEnv(config)
    }

    throw new Error(
        'getClientEnv called outside a hydrated client context. This ' +
            'function is for non-React code paths that run after hydration — ' +
            'error boundaries (error.tsx, global-error.tsx) and event handlers ' +
            "on already-mounted components. If you're calling this from a " +
            "render path or a 'use client' module's top level, move the call " +
            'to a useEffect or an event handler. If you reached this from ' +
            'global-error.tsx, ensure the locale layout mounts ClientConfigScript ' +
            'in <head>.',
    )
}
